import { gql, useMutation, useQuery } from '@apollo/client';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogTitle,
  Button,
  ButtonGroup,
} from '@apollo/orbit';
import React, { useRef } from 'react';

import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { EditorTabState } from '../../../hooks/useExplorerState/useEditorTabState/useEditorTabState';

export const DeleteCollectionEntryModal = ({
  collectionId,
  collectionEntryId,
  dispatchSavedOperationLocalStateAction,
  isOpen,
  onClose,
}: {
  collectionId: string;
  collectionEntryId: string;
  dispatchSavedOperationLocalStateAction: EditorTabState['dispatchSavedOperationLocalStateAction'];
  isOpen: boolean;
  onClose: () => void;
}) => {
  const trackCustomEvent = useTrackCustomEvent();

  const { data, loading: dataLoading } = useQuery<
    GraphQLTypes.DeleteCollectionEntryModalQuery,
    GraphQLTypes.DeleteCollectionEntryModalQueryVariables
  >(
    gql`
      query DeleteCollectionEntryModalQuery(
        $collectionId: ID!
        $collectionEntryId: ID!
      ) {
        operationCollection(id: $collectionId) {
          ... on OperationCollection {
            id
            isShared
            isSandbox
            operation(id: $collectionEntryId) {
              ... on OperationCollectionEntry {
                id
                name
              }
            }
          }
        }
      }
    `,
    { variables: { collectionId, collectionEntryId } },
  );

  const [deleteOperationMutation] = useMutation<
    GraphQLTypes.DeleteOperation,
    GraphQLTypes.DeleteOperationVariables
  >(
    gql`
      mutation DeleteOperation($collectionId: ID!, $collectionEntryId: ID!) {
        operationCollection(id: $collectionId) {
          deleteOperation(id: $collectionEntryId) {
            ... on OperationCollection {
              id
              isSandbox
              isShared
              operations {
                id
              }
            }
          }
        }
      }
    `,
    {
      variables: { collectionId, collectionEntryId },
      onError: (error) => setMutationErrorMessage(error.message),
    },
  );

  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const [mutationLoading, setMutationLoading] = React.useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] =
    React.useState<string>();

  const loading = dataLoading || mutationLoading;
  const collection =
    data?.operationCollection.__typename === 'OperationCollection'
      ? data.operationCollection
      : undefined;
  const collectionEntry =
    collection?.operation?.__typename === 'OperationCollectionEntry'
      ? collection.operation
      : undefined;

  const handleSubmit = React.useCallback(async () => {
    setMutationLoading(true);

    try {
      const result = (await deleteOperationMutation()).data?.operationCollection
        ?.deleteOperation;
      if (result?.__typename !== 'OperationCollection') {
        setMutationErrorMessage('Could not delete operation');
      } else {
        dispatchSavedOperationLocalStateAction({
          type: 'DeleteOperation',
          collectionEntryId,
        });
        trackCustomEvent({
          action: 'delete_collection_entry',
          category: 'Operation Collections',
          label: result.isSandbox
            ? 'sandbox'
            : result.isShared
              ? 'shared'
              : 'personal',
        });
        onClose();
      }
    } catch {
      // Captured via `onError`
    }

    setMutationLoading(false);
  }, [
    deleteOperationMutation,
    dispatchSavedOperationLocalStateAction,
    collectionEntryId,
    trackCustomEvent,
    onClose,
  ]);

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      leastDestructiveRef={cancelButtonRef}
    >
      <AlertDialogOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate={true}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {collectionEntry ? (
                <div>
                  Are you sure you want to delete the{' '}
                  <span className="italic">{collectionEntry.name}</span>{' '}
                  operation?
                </div>
              ) : (
                'Are you sure you want to delete this operation?'
              )}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            {loading ? (
              <Loading />
            ) : !collectionEntry ? (
              <div>Could not fetch details for operation</div>
            ) : (
              <>
                Once deleted, this operation cannot be recovered.
                {mutationErrorMessage && (
                  <ErrorMessage>{mutationErrorMessage}</ErrorMessage>
                )}
              </>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup>
              <Button
                ref={cancelButtonRef}
                variant="secondary"
                onClick={onClose}
                type="button"
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                isDisabled={!collectionEntry}
                isLoading={loading}
                loadingText="Yes, delete"
                type="submit"
              >
                Yes, delete
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </CustomForm>
    </AlertDialog>
  );
};
